/* CCTV overlay, following TITAN_dev's CctvPanel: a translucent dark panel
   docked to the right edge of the map, sliding in when a camera is picked,
   with a chevron tab to reopen it and a full-width mode for watching
   several streams at once. */

.cctv-stage {
    position: relative;
    overflow: hidden;
    border-radius: var(--radius-md);
}

.cctv-panel {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: var(--map-panel-width);
    z-index: var(--z-map-panel);
    display: flex;
    flex-direction: column;
    background: var(--map-panel-bg);
    backdrop-filter: blur(10px);
    border-left: 1px solid var(--map-panel-border);
    color: var(--map-panel-ink);
    font-family: var(--font-sans);
    transform: translateX(100%);
    transition: transform 0.3s ease, width 0.3s ease;
}

.cctv-panel--open {
    transform: translateX(0);
}

.cctv-panel--wide {
    width: 100%;
    border-left: none;
}

/* The tab that pulls the panel back out. */
.cctv-panel-tab {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: var(--z-map-overlay);
    width: 36px;
    height: 84px;
    padding: 0;
    border: none;
    cursor: pointer;
    color: #fff;
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(6px);
    transition: right 0.3s ease, background 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
}

.cctv-panel-tab:hover {
    background: rgba(0, 0, 0, 0.45);
    box-shadow: 0 0 16px rgba(255, 165, 0, 0.85);
}

.cctv-panel-tab--closed {
    right: 0;
    border-radius: 0 8px 8px 0;
}

.cctv-panel-tab--open {
    right: var(--map-panel-width);
    border-radius: 8px 0 0 8px;
}

.cctv-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    /* The title yields before the controls do: it used to push them past
       the panel edge, where the stage's overflow clipped them. */
    flex-wrap: nowrap;
    padding: var(--space-3);
    background: var(--map-panel-chrome);
    border-bottom: 1px solid var(--map-panel-border);
}

.cctv-panel__title {
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--text-title);
    font-weight: var(--weight-semibold);
    color: var(--map-panel-ink);
}

.cctv-panel__controls {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex: none;
}

/* Its own row, full width: nothing can squeeze it out of the header. */
.cctv-panel__toolbar {
    padding: var(--space-3) var(--space-3) 0;
}

.cctv-panel__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    color: #fff;
    border-radius: var(--radius-sm);
    background: rgba(255, 255, 255, 0.1);
    width: 1.9rem;
    height: 1.9rem;
    font-size: 1rem;
}

.cctv-panel__btn:hover {
    background: rgba(255, 255, 255, 0.2);
}

.cctv-panel__btn--danger,
.cctv-panel__close-all {
    background: var(--map-panel-danger);
}

.cctv-panel__btn--danger:hover,
.cctv-panel__close-all:hover {
    background: var(--map-panel-danger-hover);
}

.cctv-panel__close-all {
    width: 100%;
    height: 2.25rem;
    padding: 0 var(--space-3);
    border: none;
    border-radius: var(--radius-sm);
    color: #fff;
    font-family: var(--font-sans);
    font-size: var(--text-label);
    cursor: pointer;
}

.cctv-panel__body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
}

/* Wide mode fits several streams across instead of one column. */
.cctv-panel--wide .cctv-panel__body {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    align-content: start;
}

.cctv-panel__empty {
    margin-top: var(--space-4);
    text-align: center;
    color: var(--map-panel-ink-muted);
}

.cctv-card {
    background: var(--map-panel-inset);
    border-radius: var(--radius-md);
    padding: var(--space-2);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.cctv-card__title {
    color: #fff;
    font-weight: var(--weight-semibold);
    font-size: var(--text-body);
}

.cctv-card__stream {
    width: 100%;
    background: #000;
    border-radius: var(--radius-sm);
    overflow: hidden;
    aspect-ratio: 16 / 9;
}

.cctv-card__close {
    align-self: flex-end;
    border: none;
    border-radius: var(--radius-sm);
    padding: var(--space-1) var(--space-3);
    background: var(--map-panel-danger);
    color: #fff;
    font-family: var(--font-sans);
    font-size: var(--text-label);
    cursor: pointer;
}

.cctv-card__close:hover {
    background: var(--map-panel-danger-hover);
}

@media (max-width: 720px) {
    .cctv-panel {
        width: 100%;
    }

    .cctv-panel-tab--open {
        right: 0;
        border-radius: 8px 0 0 8px;
    }
}

.cctv-panel-tab__count {
    position: absolute;
    top: 6px;
    right: 4px;
    min-width: 1.15rem;
    height: 1.15rem;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--map-panel-danger);
    color: #fff;
    font-family: var(--font-sans);
    font-size: 0.7rem;
    font-weight: var(--weight-semibold);
    display: flex;
    align-items: center;
    justify-content: center;
}
